import React, { useRef, useState } from 'react';
import { Button, Grid, OutlinedInput, InputLabel, Stack, FormHelperText, Box, Typography, CircularProgress } from '@mui/material';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTheme } from '@mui/material/styles';
import testLogoImg from 'assets/images/icons/test_logo.png';
import testThumbImg from 'assets/images/icons/test_thum.png';
import { SUPPORT_IMAGE_TYPE, maxFileSizeAllow } from 'constant/AppConfig';
import { toast } from 'react-toastify';
import { apiRequest } from 'helpers/apiRequests';
import { useNavigate } from 'react-router-dom';
import ImageCropper from 'components/@extended/ImageCropper';
import { LoadingButton } from '@mui/lab';

const Theme = ({ handleBack, themeInfo, setThemeInfo, companyInfo, playbook, setIsDirty }) => {
  const theme = useTheme();
  const logoInputRef = useRef(null);
  const inputOneRef = useRef(null);
  const inputTwoRef = useRef(null);
  const thumbInputRef = useRef(null);
  const navigate = useNavigate();

  const [primary, setPrimary] = useState('#0000');
  const [secondary, setSecondary] = useState('#0000');
  const [logo, setLogo] = useState('');
  const [thumbnail, setThumbnail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenModal, setOpenModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [file, setFile] = useState();

  const boxStyle = {
    height: '60px',
    border: `1px dashed ${theme.palette.primary.main}`,
    background: theme.palette.primary.lighter,
    color: theme.palette.primary.main,
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    padding: { xs: '0px 11px', sm: '0px 21px' },
    cursor: 'pointer'
  };

  const inputStyle = {
    width: '35px',
    height: '35px',
    padding: '0px',
    // borderRadius: '50% !important',
    overflow: 'hidden',
    position: 'relative',
    input: {
      padding: '0px !important',
      width: '35px',
      height: '35px',
      // borderRadius: '50% !important',
      zIndex: 1,
      outline: 'none'
    },
    fieldset: {
      border: 'none !important',
      outline: 'none'
    }
  };

  const validationSchema = Yup.object().shape({
    // company_logo: Yup.string().required('Company logo is required.'),
    // company_thumbnail: Yup.string().required('Company thumbnail is required.'),
    primary_color: Yup.string().required('Primary color is required.'),
    secondary_color: Yup.string().required('Secondary color is required.')
  });

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      company_logo: '',
      company_thumbnail: '',
      primary_color: '',
      secondary_color: ''
    }
  });

  const onSubmit = async (values) => {
    try {
      setIsDirty(false);
      setIsSubmitting(true);
      const reqObj = {
        // is_demo_company: companyInfo?.is_demo_company,
        // tenant_scale: companyInfo?.tenant_scale,
        company: {
          fiscal_start_month: companyInfo?.fiscal_start_month,
          support_email: companyInfo?.support_email,
          unique_url_component: companyInfo?.unique_url_component,
          web_url: companyInfo?.web_url,
          company_name: companyInfo?.company_name,
          is_demo_company: companyInfo?.is_demo_company
        },
        group_of_companies: companyInfo?.group_of_companies?.map((item) => item.tenant_id),
        // branches: branchInfo?.branches,
        // departments: departments?.departments,
        playbook: playbook,
        theme_configuration: {
          theme_id: 1,
          tenant_theme_configuration_value: {
            ...values
          }
        }
        // subscription_plans: companyInfo?.subscription_plans,
        // update_frequency: companyInfo?.update_frequency
      };

      const result = await apiRequest.addCompany(reqObj);
      if (result?.data?.status === 200) {
        toast.success(result?.data?.message);
        navigate('/companies', { replace: true });
      }
      setThemeInfo({ ...values, logo: logo, thumbnail: thumbnail });
    } catch (error) {
      setIsDirty(true);
      if (error?.response?.data?.meta_data?.error_fields) {
        // toast.error(showErrors(error?.response?.data?.meta_data?.error_fields), {
        //   autoClose: 6000
        // });
      } else {
        toast.error(error?.response?.data?.message);
      }
      console.log('error: ', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = async (e, type) => {
    try {
      const file = e.target.files;
      if (file) {
        if (!SUPPORT_IMAGE_TYPE.includes(file[0]?.type)) {
          toast.error('Uploaded file is not image!');
          if (type === 'logo') {
            logoInputRef.current.value = '';
          } else {
            thumbInputRef.current.value = '';
            setValue('company_thumbnail', '');
          }
          return;
        }
        if (file[0].size >= maxFileSizeAllow * 1024 * 1024) {
          if (type === 'logo') {
            logoInputRef.current.value = '';
          } else {
            thumbInputRef.current.value = '';
            setValue('company_thumbnail', '');
          }
          toast.error(`Uploaded image is more than ${maxFileSizeAllow} MB.`);
          return;
        }
        setFile(file[0]);
        setOpenModal(type);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
      if (type === 'logo') {
        setValue('company_logo', '');
      } else {
        setValue('company_thumbnail', '');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (type) => {
    if (type === 'logo') {
      logoInputRef.current.click();
    } else {
      thumbInputRef.current.click();
    }
  };

  const handleClose = async (file) => {
    const imageType = isOpenModal;
    setOpenModal(false);
    try {
      if (file === '') throw Error('');
      if (imageType === 'logo') {
        setIsLoading('logo');
        const response = await apiRequest.uploadProfileImage(file, 'tenant-logo', 'logo');
        if (response?.data.status === 200) {
          toast.success(response?.data?.message);
          setValue('company_logo', response?.data?.data?.image_url);
          setError('company_logo', '');
          setLogo(response?.data?.data);
          setThemeInfo((prev) => ({ ...prev, logo: response?.data?.data }));
        }
      } else {
        setIsLoading('thumbnail');
        const response = await apiRequest.uploadProfileImage(file, 'tenant-logo', 'thumbnail');
        if (response?.data.status === 200) {
          toast.success(response?.data?.message);
          setError('company_thumbnail', '');
          setValue('company_thumbnail', response?.data?.data?.image_url);
          setThumbnail(response?.data?.data);
          setThemeInfo((prev) => ({ ...prev, thumbnail: response?.data?.data }));
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
      if (error?.response?.status === 413) {
        toast.error('Uploaded image is more than 5 MB.');
      }
      if (imageType === 'logo') {
        logoInputRef.current.value = null;
      } else {
        thumbInputRef.current.value = null;
      }
    } finally {
      setFile(null);
      setOpenModal(false);
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (themeInfo) {
      setValue('company_logo', themeInfo?.logo?.image_name);
      setValue('company_thumbnail', themeInfo?.thumbnail?.image_name);
      setValue('primary_color', themeInfo?.primary_color);
      setValue('secondary_color', themeInfo?.secondary_color);
      setLogo(themeInfo?.logo);
      setThumbnail(themeInfo?.thumbnail);
      setPrimary(themeInfo?.primary_color);
      setSecondary(themeInfo?.secondary_color);
    }
  }, [themeInfo]);

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <Box>
        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            xs={12}
          >
            <Stack spacing={1}>
              <InputLabel htmlFor="mission">Company Logo</InputLabel>
              <Box
                sx={boxStyle}
                onClick={() => handleFileUpload('logo')}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  width={'100%'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                >
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ flexWrap: { xs: 'wrap' } }}
                  >
                    <Typography
                      fontWeight={600}
                      variant="subtitle1"
                      sx={{ fontSize: { xs: '12px' } }}
                    >
                      Upload logo (128*44 px)
                    </Typography>
                    <Typography
                      fontWeight={300}
                      fontSize={12}
                      lineHeight={'21px'}
                      variant="subtitle2"
                      sx={{ fontSize: { xs: '8px' }, lineHeight: { xs: '10px' } }}
                    >
                      {logo?.image_name ? '' : 'Upload rectangular logo for company. Example:'}
                    </Typography>
                  </Stack>
                  {isLoading === 'logo' ? (
                    <Box sx={{ width: 'auto', height: 'auto', textAlign: 'center' }}>
                      <CircularProgress
                        sx={{ width: '25px !important', height: '25px !important' }}
                        color="primary"
                      />
                    </Box>
                  ) : (
                    <img
                      src={logo?.image_url ?? testLogoImg}
                      alt="logo"
                      style={{ width: 'auto', height: '44px' }}
                    />
                  )}
                  <input
                    style={{ display: 'none' }}
                    type="file"
                    ref={logoInputRef}
                    id="fileupload"
                    onChange={(e) => handleFileChange(e, 'logo')}
                  />
                </Stack>
              </Box>
              {errors.company_logo?.message && (
                <FormHelperText
                  error
                  id="standard-weight-helper-text-company_logo"
                >
                  {errors.company_logo?.message}
                </FormHelperText>
              )}
            </Stack>
            {/* <input
              type="file"
              accept="image/*"
              name="logo"
              id="logo"
            /> */}
          </Grid>
          <Grid
            item
            xs={12}
          >
            <Stack spacing={1}>
              <InputLabel htmlFor="mission">Company Thumbnail</InputLabel>
              <Box
                sx={boxStyle}
                onClick={() => handleFileUpload('thumb')}
              >
                <Stack
                  direction="row"
                  width={'100%'}
                  spacing={1}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                >
                  <Stack
                    direction={'row'}
                    spacing={0.5}
                    sx={{ flexWrap: { xs: 'wrap' } }}
                  >
                    <Typography
                      fontWeight={600}
                      variant="subtitle1"
                      sx={{ fontSize: { xs: '12px' } }}
                    >
                      Upload thumbnail (64*64 px)
                    </Typography>
                    <Typography
                      fontWeight={300}
                      fontSize={12}
                      lineHeight={'21px'}
                      variant="subtitle2"
                      sx={{ fontSize: { xs: '8px' }, lineHeight: { xs: '10px' } }}
                    >
                      {thumbnail?.image_name ? '' : 'Upload square logo for company. Example:'}
                    </Typography>
                  </Stack>
                  {isLoading === 'thumbnail' ? (
                    <Box sx={{ width: '44px', height: 'auto' }}>
                      <CircularProgress
                        sx={{ width: '25px !important', height: '25px !important' }}
                        color="primary"
                      />
                    </Box>
                  ) : (
                    <img
                      style={{ width: '44px', height: 'auto' }}
                      src={thumbnail?.image_url ?? testThumbImg}
                      alt="logo"
                    />
                  )}
                </Stack>
                <input
                  style={{ display: 'none' }}
                  type="file"
                  ref={thumbInputRef}
                  id="fileupload"
                  onChange={(e) => handleFileChange(e, 'thumb')}
                />
              </Box>
              {errors.company_thumbnail?.message && (
                <FormHelperText
                  error
                  id="standard-weight-helper-text-company_thumbnail"
                >
                  {errors.company_thumbnail?.message}
                </FormHelperText>
              )}
            </Stack>
            {/* <input
              type="file"
              accept="image/*"
              name="shortLogo"
              id="shortLogo"
            /> */}
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
          >
            <Stack spacing={1}>
              <InputLabel htmlFor="mission">Primary Color</InputLabel>
              <Box
                sx={{
                  borderRadius: '8px',
                  border: '1px solid #CBD5E1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '5px 10px',
                  cursor: 'pointer'
                }}
                onClick={() => inputOneRef.current?.click()}
              >
                <Typography
                  variant="subtitle1"
                  color="secondary.light"
                >
                  {primary}{' '}
                </Typography>
                <Controller
                  name="primary_color"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <OutlinedInput
                      ref={inputOneRef}
                      id="primary_color"
                      type="color"
                      value={field.value}
                      name={field.name}
                      onBlur={field.onBlur}
                      onChange={(e) => {
                        field.onChange(e);
                        setTimeout(() => {
                          setThemeInfo((prev) => ({ ...prev, primary_color: e.target.value }));
                          setPrimary(e.target.value);
                        }, 1500);
                      }}
                      placeholder="Company Name"
                      fullWidth
                      sx={inputStyle}
                      error={Boolean(errors.primary_color)}
                    />
                  )}
                />
              </Box>
              {errors.primary_color?.message && (
                <FormHelperText
                  error
                  id="standard-weight-helper-text-primary_color"
                >
                  {errors.primary_color?.message}
                </FormHelperText>
              )}
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
          >
            <Stack spacing={1}>
              <InputLabel htmlFor="mission">Secondary Color</InputLabel>
              <Box
                sx={{
                  borderRadius: '8px',
                  border: '1px solid #CBD5E1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '5px 10px',
                  cursor: 'pointer'
                }}
              >
                <Typography
                  variant="subtitle1"
                  color="secondary.light"
                >
                  {secondary}
                </Typography>
                <Controller
                  name="secondary_color"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <OutlinedInput
                      ref={inputTwoRef}
                      id="secondary_color"
                      type="color"
                      value={field.value}
                      name={field.name}
                      onBlur={field.onBlur}
                      onChange={(e) => {
                        field.onChange(e);
                        setTimeout(() => {
                          setSecondary(e.target.value);
                          setThemeInfo((prev) => ({ ...prev, secondary_color: e.target.value }));
                        }, 1500);
                      }}
                      placeholder="Company Name"
                      fullWidth
                      sx={inputStyle}
                      error={Boolean(errors.secondary_color)}
                    />
                  )}
                />
              </Box>

              {errors.secondary_color?.message && (
                <FormHelperText
                  error
                  id="standard-weight-helper-text-secondary_color"
                >
                  {errors.secondary_color?.message}
                </FormHelperText>
              )}
              {/* <OutlinedInput
                placeholder="Secondary Color"
                name="secondaryColor"
                fullWidth
                type="color"
                //   value={formik.values.secondaryColor}
                //   onChange={formik.handleChange}
                //   error={formik.touched.secondaryColor && Boolean(formik.errors.secondaryColor)}
                //   helperText={formik.touched.secondaryColor && formik.errors.secondaryColor}
              /> */}
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              spacing={1}
            >
              <Button
                disabled={isSubmitting}
                onClick={handleBack}
                variant="outlined"
                color="secondary"
                sx={{ width: '200px', height: '40px' }}
              >
                Back
              </Button>
              <LoadingButton
                disableElevation
                loading={isSubmitting}
                loadingPosition="start"
                disabled={isSubmitting}
                variant="contained"
                color="primary"
                type="submit"
                sx={{ width: '200px', height: '40px' }}
              >
                Save
              </LoadingButton>
            </Stack>
          </Grid>
        </Grid>
        <ImageCropper
          image={file}
          isOpenModal={isOpenModal}
          handleClose={handleClose}
          cropRatio={isOpenModal === 'logo' ? 3 / 1 : 1 / 1}
        />
      </Box>
    </form>
  );
};

export default Theme;
